import type { Path, PathMarker, Waypoint } from '../../Components/PathBuilder/pathBuilderTypes';
import { PATH_MARKER_TYPES } from '../../Components/PathBuilder/pathBuilderTypes';
import { PATH_BUILDER_SPEED_CONSTRAINTS } from '../constants';
import { assignPathAndWaypointIds, canonicalizeRotationDegrees, CM_PER_M, getPathWaypoints } from './path-builder-helpers';
import { roundCoordinateMeters } from '../fieldSelection';

export const DEFAULT_PATH_SEGMENT_SPEED = 1;

export function clampSegmentSpeed(value: number): number {
  return Math.min(PATH_BUILDER_SPEED_CONSTRAINTS.max, Math.max(PATH_BUILDER_SPEED_CONSTRAINTS.min, value));
}

/** Accepts waypoints that may carry legacy field names (radius/rotation) or a per-waypoint speed. */
type LegacyWaypoint = {
  id?: string;
  x?: unknown;
  y?: unknown;
  rad?: unknown;
  rot?: unknown;
  /** Legacy field name for rad */
  radius?: unknown;
  /** Legacy field name for rot */
  rotation?: unknown;
  /** Legacy per-waypoint speed */
  speed?: unknown;
};

/**
 * Parse a persisted / imported value as a finite number.
 * Accepts finite numbers and numeric strings (e.g. `"270"`); returns undefined otherwise.
 */
function tryFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return undefined;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/** Coerce to a finite number, defaulting missing / non-numeric values to `fallback`. */
function toFiniteNumber(value: unknown, fallback = 0): number {
  return tryFiniteNumber(value) ?? fallback;
}

/** Legacy waypoint keys that must be removed during compact migration. */
const LEGACY_WAYPOINT_KEYS = ['radius', 'rotation', 'speed'] as const;

/** Legacy path keys that must be removed during compact migration. */
const LEGACY_PATH_KEYS = ['waypoints', 'segmentSpeeds'] as const;

function omitKeys<T extends Record<string, unknown>>(value: T, keys: readonly string[]): Record<string, unknown> {
  const next: Record<string, unknown> = { ...value };
  for (const key of keys) {
    delete next[key];
  }
  return next;
}

/**
 * During compact migration, prefer legacy aliases when present so a stale compact
 * field (e.g. `rot: 0` alongside `rotation: 270`) cannot win via `??`.
 */
function pickLegacyOrCompactField(raw: LegacyWaypoint, legacyKey: 'radius' | 'rotation', compactKey: 'rad' | 'rot'): unknown {
  return legacyKey in raw ? raw[legacyKey] : raw[compactKey];
}

/**
 * Normalize known waypoint fields and strip legacy aliases, while preserving any
 * future-schema fields already present on the waypoint.
 */
function normalizeLegacyWaypoint(raw: LegacyWaypoint): Waypoint {
  const preserved = omitKeys(raw as Record<string, unknown>, LEGACY_WAYPOINT_KEYS);
  return {
    ...preserved,
    id: typeof raw.id === 'string' ? raw.id : '',
    x: Math.round(toFiniteNumber(raw.x) * 10) / 10,
    y: Math.round(toFiniteNumber(raw.y) * 10) / 10,
    rad: Math.round(toFiniteNumber(pickLegacyOrCompactField(raw, 'radius', 'rad'))),
    rot: canonicalizeWaypointRotation(pickLegacyOrCompactField(raw, 'rotation', 'rot')),
  } as Waypoint;
}

/** Disk/legacy path shape before normalization. */
type LegacyPathInput = Omit<Path, 'spd' | 'wp' | 'mrk'> & {
  wp?: LegacyWaypoint[];
  /** Legacy path-level key (pre-wp rename) */
  waypoints?: LegacyWaypoint[];
  /** Legacy path-level key (pre-spd rename) */
  segmentSpeeds?: unknown[];
  spd?: unknown[];
  mrk?: PathMarker[];
};

function isPersistedFiniteNumber(value: unknown): boolean {
  return typeof value === 'number' && Number.isFinite(value);
}

function legacyWaypointNeedsMigration(raw: LegacyWaypoint): boolean {
  return 'radius' in raw || 'rotation' in raw || 'speed' in raw;
}

/** True when compact fields are present but not already finite numbers (e.g. `"17.5"`). */
function waypointNeedsNumericCoercion(raw: LegacyWaypoint): boolean {
  return (
    !isPersistedFiniteNumber(raw.x) ||
    !isPersistedFiniteNumber(raw.y) ||
    !isPersistedFiniteNumber(pickLegacyOrCompactField(raw, 'radius', 'rad')) ||
    !isPersistedFiniteNumber(pickLegacyOrCompactField(raw, 'rotation', 'rot'))
  );
}

/** True when a path still uses legacy keys or has non-numeric compact fields that need coercion. */
export function pathNeedsMigration(path: LegacyPathInput): boolean {
  if (pathNeedsLegacyKeyMigration(path)) return true;
  const raw = path.wp ?? [];
  if (raw.some(waypointNeedsNumericCoercion)) return true;
  return (path.spd ?? []).some((s) => !isPersistedFiniteNumber(s));
}

/** True when a path still has legacy aliases that compact migration must rename/strip. */
function pathNeedsLegacyKeyMigration(path: LegacyPathInput): boolean {
  if (path.waypoints !== undefined) return true;
  if (path.segmentSpeeds !== undefined) return true;
  return (path.wp ?? []).some(legacyWaypointNeedsMigration);
}

/**
 * Normalize a path from disk or legacy shape: rename keys, round values, strip per-waypoint speed, sync spd.
 *
 * INVARIANT: this is structural decode/normalization only — it must stay value-preserving and idempotent, and
 * it is NOT version-gated. Do not add value transforms (e.g. unit conversions such as cm↔m) here; those belong
 * in a version-gated PROJECT_MIGRATION_STEPS step run once via `migrateProject`. `migratePath` can be
 * called on already-normalized data (parse, reducers) without gating, so a non-idempotent transform here would
 * apply multiple times and corrupt path data.
 */
export function migratePath(path: LegacyPathInput): Path {
  // Same non-empty preference as getPathWaypoints so empty wp cannot drop legacy waypoints.
  const raw = getPathWaypoints(path);
  const normalizedWp = raw.map(normalizeLegacyWaypoint);
  const legacySegmentSpeeds = path.segmentSpeeds;

  const n = Math.max(0, normalizedWp.length - 1);
  const rawSpeeds = path.spd ?? legacySegmentSpeeds ?? [];
  let spd: number[];

  if (rawSpeeds.length !== n) {
    spd = [];
    for (let i = 0; i < n; i++) {
      const fromSeg = path.spd?.[i] ?? legacySegmentSpeeds?.[i];
      const fromWp = raw[i]?.speed;
      const val = tryFiniteNumber(fromSeg) ?? tryFiniteNumber(fromWp) ?? DEFAULT_PATH_SEGMENT_SPEED;
      spd.push(clampSegmentSpeed(val));
    }
  } else {
    spd = rawSpeeds.map((s) => clampSegmentSpeed(tryFiniteNumber(s) ?? DEFAULT_PATH_SEGMENT_SPEED));
  }

  // Preserve future-schema path fields; drop only legacy aliases. Keep mrk omit-when-empty behavior.
  const preserved = omitKeys(path as Record<string, unknown>, LEGACY_PATH_KEYS);
  delete preserved.wp;
  delete preserved.spd;
  delete preserved.mrk;

  const migrated = {
    ...preserved,
    name: path.name,
    id: path.id,
    isVisible: path.isVisible,
    wp: normalizedWp,
    spd,
    ...(path.mrk?.length ? { mrk: path.mrk } : {}),
  } as Path;
  return assignPathAndWaypointIds(migrated);
}

/** True when any path still uses legacy keys or non-numeric compact fields. */
export function projectPathsNeedStructuralMigration(project: { paths?: Path[] }): boolean {
  return (project.paths ?? []).some((path) => pathNeedsMigration(path as LegacyPathInput));
}

/** True when any path still has legacy `waypoints`/`segmentSpeeds` or radius/rotation/speed aliases. */
export function projectPathsNeedLegacyKeyMigration(project: { paths?: Path[] }): boolean {
  return (project.paths ?? []).some((path) => pathNeedsLegacyKeyMigration(path as LegacyPathInput));
}

/** Rewrites legacy path data to the compact on-disk shape. Does not update project version. */
export function applyCompactPathMigration<T extends { paths?: Path[]; pathBuilderFieldRotation?: number; selectedPathId?: string }>(project: T): T {
  const rawPaths = project.paths ?? [];
  const pathBuilderFieldRotation = project.pathBuilderFieldRotation ?? 0;

  if (!rawPaths.length) {
    return { ...project, pathBuilderFieldRotation } as T;
  }

  const migratedPaths = rawPaths.map((p) => migratePath(p as LegacyPathInput));

  let selectedPathId = project.selectedPathId ?? '';
  if (selectedPathId && !migratedPaths.some((p) => p.id === selectedPathId)) {
    const oldIndex = rawPaths.findIndex((p) => p.id === selectedPathId);
    selectedPathId = oldIndex >= 0 ? migratedPaths[oldIndex].id : migratedPaths[0]?.id ?? '';
  }

  return {
    ...project,
    paths: migratedPaths,
    selectedPathId,
    pathBuilderFieldRotation,
  } as T;
}

/**
 * Coerce a persisted heading to a finite number (missing / null / NaN / non-numeric → 0),
 * matching legacy `normalizeLegacyWaypoint` defaulting, then canonicalize to [−180, 180).
 * Numeric strings such as `"270"` are coerced and canonicalized (→ −90).
 */
function canonicalizeWaypointRotation(rot: unknown): number {
  return canonicalizeRotationDegrees(toFiniteNumber(rot));
}

/** Round coordinates the same way compact migration does when coercing non-numeric disk values. */
function coercePersistedCoordinate(value: unknown): number {
  return Math.round(toFiniteNumber(value) * 10) / 10;
}

function coercePersistedRadius(value: unknown): number {
  return Math.round(toFiniteNumber(value));
}

/** True when a waypoint heading is not already in canonical signed form (incl. +180 → −180). */
function waypointNeedsSignedHeadingMigration(wp: Waypoint): boolean {
  return canonicalizeWaypointRotation(wp.rot) !== wp.rot;
}

/** True when compact waypoint fields need numeric coercion and/or signed-heading canonicalize. */
function waypointNeedsSignedHeadingFieldMigration(wp: Waypoint): boolean {
  return (
    !isPersistedFiniteNumber(wp.x) || !isPersistedFiniteNumber(wp.y) || !isPersistedFiniteNumber(wp.rad) || waypointNeedsSignedHeadingMigration(wp)
  );
}

function pathNeedsSignedHeadingMigration(path: Path): boolean {
  if ((path.wp ?? []).some(waypointNeedsSignedHeadingFieldMigration)) return true;
  const wpCount = (path.wp ?? []).length;
  const spd = path.spd ?? [];
  if (spd.some((speed) => !isPersistedFiniteNumber(speed))) return true;
  // Align with migratePath: spd length must be waypointCount - 1 (when waypoints exist).
  if (wpCount >= 2 && spd.length !== wpCount - 1) return true;
  if (wpCount < 2 && spd.length !== 0) return true;
  return false;
}

/** True when any path needs numeric field coercion or signed-heading canonicalize for 1.0.2. */
export function projectPathsNeedSignedHeadingMigration(project: { paths?: Path[] }): boolean {
  return (project.paths ?? []).some((path) => pathNeedsSignedHeadingMigration(path));
}

/**
 * Coerce compact numeric fields to finite numbers and canonicalize `rot`.
 * Keeps already-finite x/y/rad values unchanged (no re-rounding). Never regenerates ids.
 */
function migrateWaypointSignedHeadingFields(waypoint: Waypoint): Waypoint {
  const x = isPersistedFiniteNumber(waypoint.x) ? waypoint.x : coercePersistedCoordinate(waypoint.x);
  const y = isPersistedFiniteNumber(waypoint.y) ? waypoint.y : coercePersistedCoordinate(waypoint.y);
  const rad = isPersistedFiniteNumber(waypoint.rad) ? waypoint.rad : coercePersistedRadius(waypoint.rad);
  const rot = canonicalizeWaypointRotation(waypoint.rot);
  if (x === waypoint.x && y === waypoint.y && rad === waypoint.rad && rot === waypoint.rot) {
    return waypoint;
  }
  return { ...waypoint, x, y, rad, rot };
}

function migratePathSignedHeadings(path: Path): Path {
  if (!pathNeedsSignedHeadingMigration(path)) {
    return path;
  }

  const wp = (path.wp ?? []).map(migrateWaypointSignedHeadingFields);
  const coercedSpd = (path.spd ?? []).map((speed) =>
    isPersistedFiniteNumber(speed) ? speed : clampSegmentSpeed(toFiniteNumber(speed, DEFAULT_PATH_SEGMENT_SPEED))
  );
  const spd = syncSegmentSpeedsLen(coercedSpd, wp.length);

  const wpUnchanged = wp.length === (path.wp ?? []).length && wp.every((waypoint, i) => waypoint === path.wp?.[i]);
  const spdUnchanged = spd.length === (path.spd ?? []).length && spd.every((speed, i) => speed === path.spd?.[i]);
  if (wpUnchanged && spdUnchanged) {
    return path;
  }

  return {
    ...path,
    wp,
    spd,
  };
}

/**
 * 1.0.2 upgrade: canonicalize `rot` to signed [−180, 180) and coerce non-numeric compact fields.
 * If a project was stamped 1.0.1 but still has legacy key aliases, run compact first so those
 * become `wp`/`spd` before heading repair. Clean heading-only / numeric-string upgrades do not
 * regenerate ids.
 */
export function applySignedHeadingMigration<T extends { paths?: Path[]; pathBuilderFieldRotation?: number; selectedPathId?: string }>(project: T): T {
  let next = project;
  if (projectPathsNeedLegacyKeyMigration(next)) {
    next = applyCompactPathMigration(next);
  }

  const paths = next.paths ?? [];
  if (!paths.length) return next;

  let changed = false;
  const nextPaths = paths.map((path) => {
    const migrated = migratePathSignedHeadings(path);
    if (migrated !== path) changed = true;
    return migrated;
  });

  return changed ? { ...next, paths: nextPaths } : next;
}

/** True when any path has path-builder values that still need cm → meters conversion for 1.0.3. */
export function projectPathsNeedMetersCoordinateMigration(project: { paths?: Path[] }): boolean {
  return (project.paths ?? []).some((path) => (getPathWaypoints(path) as Waypoint[]).length > 0);
}

function migrateWaypointCmToMeters(waypoint: Waypoint): Waypoint {
  const x = roundCoordinateMeters((isPersistedFiniteNumber(waypoint.x) ? waypoint.x : coercePersistedCoordinate(waypoint.x)) / CM_PER_M);
  const y = roundCoordinateMeters((isPersistedFiniteNumber(waypoint.y) ? waypoint.y : coercePersistedCoordinate(waypoint.y)) / CM_PER_M);
  if (x === waypoint.x && y === waypoint.y) return waypoint;
  return { ...waypoint, x, y };
}

function migratePathMetersCoordinates(path: Path): Path {
  const source = getPathWaypoints(path) as Waypoint[];
  if (!source.length) return path;
  const wp = source.map(migrateWaypointCmToMeters);
  const unchanged = wp.length === source.length && wp.every((waypoint, i) => waypoint === source[i]);
  if (unchanged) return path;
  return { ...path, wp };
}

/**
 * 1.0.3 upgrade: convert stored path-builder distances from centimeters to meters.
 * This covers waypoint X/Y, waypoint corner radius, and marker distance values.
 */
export function applyMetersCoordinateMigration<T extends { paths?: Path[]; pathBuilderFieldRotation?: number; selectedPathId?: string }>(
  project: T
): T {
  const paths = project.paths ?? [];
  if (!paths.length) return project;

  let changed = false;
  const nextPaths = paths.map((path) => {
    let migrated = migratePathMetersCoordinates(path);
    migrated = migratePathMarkerDistances(migrated);
    migrated = migratePathWaypointRadius(migrated);
    if (migrated !== path) changed = true;
    return migrated;
  });

  return changed ? { ...project, paths: nextPaths } : project;
}

function isDistanceMarkerType(type: PathMarker['type']): boolean {
  return type === PATH_MARKER_TYPES.DISTANCE_FROM_START || type === PATH_MARKER_TYPES.DISTANCE_FROM_END;
}

function migrateMarkerCmToMeters(marker: PathMarker): PathMarker {
  if (!isDistanceMarkerType(marker.type)) return marker;
  const raw = isPersistedFiniteNumber(marker.value) ? marker.value : coercePersistedCoordinate(marker.value);
  const value = roundCoordinateMeters(raw / CM_PER_M);
  if (value === marker.value) return marker;
  return { ...marker, value };
}

function migratePathMarkerDistances(path: Path): Path {
  const markers = path.mrk;
  if (!markers?.length) return path;
  const nextMarkers = markers.map(migrateMarkerCmToMeters);
  const unchanged = nextMarkers.every((marker, index) => marker === markers[index]);
  if (unchanged) return path;
  return { ...path, mrk: nextMarkers };
}

function migrateWaypointRadCmToMeters(waypoint: Waypoint): Waypoint {
  const raw = isPersistedFiniteNumber(waypoint.rad) ? waypoint.rad : coercePersistedCoordinate(waypoint.rad);
  const rad = roundCoordinateMeters(raw / CM_PER_M);
  if (rad === waypoint.rad) return waypoint;
  return { ...waypoint, rad };
}

function migratePathWaypointRadius(path: Path): Path {
  const source = getPathWaypoints(path) as Waypoint[];
  if (!source.length) return path;
  const wp = source.map(migrateWaypointRadCmToMeters);
  const unchanged = wp.length === source.length && wp.every((waypoint, index) => waypoint === source[index]);
  if (unchanged) return path;
  return { ...path, wp };
}

/** Trim/pad segment speeds to waypointCount - 1 (append uses default). */
export function syncSegmentSpeedsLen(prev: number[] | undefined, waypointCount: number): number[] {
  const n = Math.max(0, waypointCount - 1);
  const out = [...(prev ?? [])];
  if (out.length > n) out.length = n;
  while (out.length < n) out.push(DEFAULT_PATH_SEGMENT_SPEED);
  return out.map(clampSegmentSpeed);
}

/**
 * Keep segmentSpeeds aligned when waypoints are edited (insert/remove one waypoint).
 * Uses positional comparison to find the changed index so the algorithm remains correct
 * even when waypoint ids are regenerated after insert (index-based naming scheme).
 * Falls back to default-filled array if counts change unexpectedly.
 */
export function syncSegmentSpeedsAfterWaypointEdit(prev: number[] | undefined, oldWps: Waypoint[], newWps: Waypoint[]): number[] {
  const oldN = oldWps.length;
  const newN = newWps.length;

  if (newN < 2) return [];

  if (newN === oldN) {
    return syncSegmentSpeedsLen(prev, newN);
  }

  const prevSeg = prev ?? [];

  /** Find the first position where the two arrays diverge (comparing content, not id). */
  const findDivergence = (shorter: Waypoint[], longer: Waypoint[]): number => {
    for (let j = 0; j < shorter.length; j++) {
      const a = shorter[j];
      const b = longer[j];
      if (a.x !== b.x || a.y !== b.y || a.rad !== b.rad || a.rot !== b.rot) return j;
    }
    return shorter.length;
  };

  if (newN === oldN + 1) {
    const insertAt = findDivergence(oldWps, newWps);
    if (insertAt === oldN) {
      return [...syncSegmentSpeedsLen(prevSeg, oldN), DEFAULT_PATH_SEGMENT_SPEED].map(clampSegmentSpeed);
    }
    if (insertAt === 0) {
      return [DEFAULT_PATH_SEGMENT_SPEED, ...prevSeg].map(clampSegmentSpeed);
    }
    const splitSpeed = prevSeg[insertAt - 1] ?? DEFAULT_PATH_SEGMENT_SPEED;
    const next = [...prevSeg];
    next.splice(insertAt - 1, 1, splitSpeed, splitSpeed);
    return next.map(clampSegmentSpeed);
  }

  if (newN === oldN - 1) {
    const removeAt = findDivergence(newWps, oldWps);
    const p = [...prevSeg];
    if (removeAt >= oldN) return syncSegmentSpeedsLen(prevSeg, newN);
    if (removeAt === 0) {
      p.shift();
    } else if (removeAt === oldN - 1) {
      p.pop();
    } else {
      const left = p[removeAt - 1] ?? DEFAULT_PATH_SEGMENT_SPEED;
      const right = p[removeAt] ?? DEFAULT_PATH_SEGMENT_SPEED;
      p.splice(removeAt - 1, 2, (left + right) / 2);
    }
    return p.map(clampSegmentSpeed);
  }

  return Array.from({ length: newN - 1 }, () => DEFAULT_PATH_SEGMENT_SPEED);
}
